import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { applyBulkSearchSelection, hasBulkSeparators, parseBulkSearchTokens } from '../../utils/bulkSelectMatch';
import { ALL_SENTINEL, isAllSelection, toAllSelection } from '../../utils/inventorySelection';

/**
 * Lightweight multi-select dropdown (checkbox list) used by the report filters.
 * Menu renders in a portal so parent overflow does not clip the option list.
 *
 * Default value [] = nothing selected.
 * Select All uses a sentinel so we never materialize thousands of IDs.
 */
export default function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  searchable = true,
  showSelectAll = true,
  selectAllLabel = 'Select All',
  // Let the user add a value that is not in `options` (typed or pasted). normalizeCustom cleans each entry and
  // validateCustom rejects what is not acceptable (both optional).
  allowCustom = false,
  normalizeCustom = (v) => String(v || '').trim(),
  validateCustom = (v) => Boolean(v),
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const wrapRef = useRef(null);
  const controlRef = useRef(null);
  const menuRef = useRef(null);

  const norm = useMemo(
    () => options.map((o) => (typeof o === 'object' ? o : { value: o, label: o })),
    [options]
  );

  const updateMenuPosition = useCallback(() => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const viewportPad = 10;
    const isNarrow = window.innerWidth < 768;
    // Keep Apply Filter / sticky foot visible on mobile — short scrollable menu.
    const footReserve = isNarrow ? 72 : 0;
    const hardCap = isNarrow ? Math.min(150, Math.round(window.innerHeight * 0.26)) : 360;
    const softMin = isNarrow ? 96 : 160;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad - footReserve;
    const spaceAbove = rect.top - viewportPad;
    const openUp = spaceBelow < (isNarrow ? 120 : 180) && spaceAbove > spaceBelow;
    const available = openUp ? spaceAbove - gap : spaceBelow - gap;
    const maxMenuHeight = Math.min(hardCap, Math.max(softMin, available));

    setMenuStyle({
      position: 'fixed',
      left: Math.max(viewportPad, Math.min(rect.left, window.innerWidth - rect.width - viewportPad)),
      width: Math.min(rect.width, window.innerWidth - viewportPad * 2),
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      maxHeight: maxMenuHeight,
      height: 'auto',
      overflow: 'hidden',
      zIndex: 1200,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const allMode = isAllSelection(value);
  const selectedSet = useMemo(
    () => new Set(allMode ? [] : (value || []).filter((v) => v !== ALL_SENTINEL)),
    [value, allMode]
  );

  const optionValueSet = useMemo(() => new Set(norm.map((o) => o.value)), [norm]);

  // Selected values the loaded list does not know (added by hand) — shown on top so they can be unticked.
  const customSelected = useMemo(
    () => (allowCustom && !allMode ? [...selectedSet].filter((v) => !optionValueSet.has(v)) : []),
    [allowCustom, allMode, selectedSet, optionValueSet]
  );

  // What the search box would add by hand: valid entries that are not an option and not already selected.
  const customCandidates = useMemo(() => {
    if (!allowCustom) return [];
    const out = [];
    parseBulkSearchTokens(query).forEach((token) => {
      const v = normalizeCustom(token);
      if (v && validateCustom(v) && !optionValueSet.has(v) && !selectedSet.has(v) && !out.includes(v)) out.push(v);
    });
    return out;
  }, [allowCustom, query, normalizeCustom, validateCustom, optionValueSet, selectedSet]);

  const MAX_VISIBLE_OPTIONS = 300;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Also match with dashes/spaces ignored, so a customer id typed as 1234567890 finds "(123-456-7890)".
    const compact = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
    const qc = compact(q);
    const base = !q
      ? norm
      : norm.filter((o) => String(o.label).toLowerCase().includes(q) || (qc && compact(o.label).includes(qc)));

    // Pin selected / pasted values to the top so they stay visible in long lists.
    if (allMode || selectedSet.size === 0) return base;

    const selected = [];
    const rest = [];
    for (const o of base) {
      if (selectedSet.has(o.value)) selected.push(o);
      else rest.push(o);
    }
    const order = new Map(
      (value || [])
        .filter((v) => v !== ALL_SENTINEL)
        .map((v, i) => [v, i])
    );
    selected.sort((a, b) => (order.get(a.value) ?? 0) - (order.get(b.value) ?? 0)
      || String(a.label).localeCompare(String(b.label)));
    return [...selected, ...rest];
  }, [norm, query, selectedSet, allMode, value]);

  const visibleOptions = useMemo(() => {
    if (query.trim()) return filtered;
    if (filtered.length <= MAX_VISIBLE_OPTIONS) return filtered;
    return filtered.slice(0, MAX_VISIBLE_OPTIONS);
  }, [filtered, query]);

  const selectTarget = query.trim() ? filtered : norm;
  const allSelected = selectTarget.length > 0 && (
    allMode
    || selectTarget.every((o) => selectedSet.has(o.value))
  );
  const someSelected = allMode || selectTarget.some((o) => selectedSet.has(o.value));
  const selectAllText = query.trim() && filtered.length < norm.length
    ? 'Select all shown'
    : selectAllLabel;

  /** Prefer All-sentinel over materializing the full catalog. */
  const emitChange = (next) => {
    if (!Array.isArray(next) || !norm.length) {
      onChange(next);
      return;
    }
    if (isAllSelection(next)) {
      onChange(toAllSelection());
      return;
    }
    const concrete = next.filter((v) => v !== ALL_SENTINEL);
    if (concrete.length >= norm.length) {
      const nextSet = new Set(concrete);
      if (norm.every((o) => nextSet.has(o.value))) {
        onChange(toAllSelection());
        return;
      }
    }
    onChange(concrete);
  };

  const toggle = (val) => {
    if (allMode) {
      // Leaving Select All → keep only this value.
      emitChange([val]);
      return;
    }
    if (selectedSet.has(val)) emitChange(value.filter((v) => v !== val && v !== ALL_SENTINEL));
    else emitChange([...value.filter((v) => v !== ALL_SENTINEL), val]);
  };

  const toggleAll = () => {
    if (!selectTarget.length) return;
    if (allSelected) {
      if (allMode && !query.trim()) {
        // Uncheck Select All → clear to nothing selected.
        emitChange([]);
        return;
      }
      const remove = new Set(selectTarget.map((o) => o.value));
      emitChange(value.filter((v) => v !== ALL_SENTINEL && !remove.has(v)));
      return;
    }
    // Select entire catalog → sentinel (not thousands of IDs).
    const selectingEntireCatalog = !query.trim() && selectTarget.length === norm.length;
    if (selectingEntireCatalog) {
      emitChange(toAllSelection());
      return;
    }
    emitChange([...new Set([
      ...value.filter((v) => v !== ALL_SENTINEL),
      ...selectTarget.map((o) => o.value),
    ])]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    emitChange([]);
  };

  const addCustom = (values) => {
    if (!values.length) return;
    const base = allMode ? [] : value.filter((v) => v !== ALL_SENTINEL);
    emitChange([...new Set([...base, ...values])]);
    setQuery('');
  };

  const applyBulkSearch = (text = query) => {
    const result = applyBulkSearchSelection({
      query: text,
      options: norm,
      currentValues: allMode ? [] : value.filter((v) => v !== ALL_SENTINEL),
      fieldKeys: ['value', 'label'],
    });
    if (!result) return;
    // Entries that match no option are added by hand when the field allows it.
    const custom = [];
    if (allowCustom) {
      parseBulkSearchTokens(text).forEach((token) => {
        const v = normalizeCustom(token);
        if (v && validateCustom(v) && !optionValueSet.has(v) && !custom.includes(v)
          && !norm.some((o) => String(o.label).toLowerCase() === String(token).trim().toLowerCase())) custom.push(v);
      });
    }
    if (result.appliedCount > 0 || custom.length) {
      emitChange([...new Set([...result.nextValues, ...custom])]);
      setQuery('');
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyBulkSearch();
    }
  };

  const handleSearchPaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    if (!hasBulkSeparators(pasted)) return;
    e.preventDefault();
    applyBulkSearch(`${query}${pasted}`.trim());
  };

  const label = loading
    ? 'Loading…'
    : allMode && norm.length > 0
      ? 'All selected'
      : value.length === 0 || (allMode && norm.length === 0)
        ? placeholder
        : value.length === 1
          ? (norm.find((o) => o.value === value[0])?.label ?? value[0])
          : `${value.length} selected`;

  const menu = open && menuStyle ? (
    <div className="ms-menu ms-menu-portal" ref={menuRef} style={menuStyle}>
      {searchable && (norm.length > 0 || allowCustom) && !loading && (
        <input
          className="ms-search"
          placeholder={allowCustom ? 'Type or paste values (comma-separated), press Enter…' : 'Paste exact values (comma-separated), press Enter…'}
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onPaste={handleSearchPaste}
        />
      )}
      <div className="ms-options">
        {loading ? (
          <div className="ms-loading-row">
            <span className="ms-spinner" aria-hidden />
            <span>Loading options…</span>
          </div>
        ) : filtered.length === 0 && !customCandidates.length && !customSelected.length ? (
          <div className="ms-empty">{norm.length === 0 ? 'No options available' : 'No matches'}</div>
        ) : (
          <>
            {customCandidates.length > 0 && (
              <button type="button" className="ms-option ms-option-all" onClick={() => addCustom(customCandidates)}>
                <span className="ms-option-text">
                  {`+ Add ${customCandidates.length === 1 ? `“${customCandidates[0]}”` : `${customCandidates.length} values`}`}
                </span>
              </button>
            )}
            {customSelected.map((v) => (
              <label key={`custom-${v}`} className="ms-option">
                <input type="checkbox" checked onChange={() => toggle(v)} />
                <span className="ms-option-text" title={v}>{v}</span>
              </label>
            ))}
            {showSelectAll && filtered.length > 0 && (
              <label className="ms-option ms-option-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={toggleAll}
                />
                <span className="ms-option-text ms-option-all-label">{selectAllText}</span>
              </label>
            )}
            {visibleOptions.map((o) => (
              <label key={o.value} className="ms-option">
                <input
                  type="checkbox"
                  checked={allMode || selectedSet.has(o.value)}
                  onChange={() => toggle(o.value)}
                />
                <span className="ms-option-text" title={o.label}>{o.label}</span>
              </label>
            ))}
            {!query.trim() && filtered.length > MAX_VISIBLE_OPTIONS && (
              <div className="ms-empty">
                Showing first {MAX_VISIBLE_OPTIONS} of {filtered.length}. Type to search for more…
              </div>
            )}
          </>
        )}
      </div>
      {!loading && (norm.length > 0 || customSelected.length > 0) && (
        <div className="ms-footer">
          <span className="ms-count">
            {allMode ? `All ${norm.length}` : `${value.filter((v) => v !== ALL_SENTINEL).length} of ${norm.length}`} selected
          </span>
          {(value.length > 0 || allMode) && (
            <button type="button" className="ms-clear-all" onClick={() => emitChange([])}>
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={`ms ${disabled ? 'ms-disabled' : ''} ${open ? 'ms-is-open' : ''} ${loading ? 'ms-loading' : ''}`} ref={wrapRef}>
      <button
        type="button"
        ref={controlRef}
        className={`ms-control ${open ? 'ms-open' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
        aria-busy={loading}
      >
        {loading && <span className="ms-control-spinner" aria-hidden />}
        <span className={`ms-label ${!allMode && value.length === 0 ? 'ms-placeholder' : ''}`}>
          {loading ? 'Loading…' : label}
        </span>
        {(value.length > 0 || allMode) && !disabled && !loading && (
          <span className="ms-clear" title="Clear" onClick={clearAll}>×</span>
        )}
        <span className={`ms-caret${open ? ' is-open' : ''}`} aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {menu && createPortal(menu, document.body)}
    </div>
  );
}
